import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { JobDescription } from '../../../../../../../../utils/types/JobOpenings';
import { STARSCORING } from '../Constants';

interface Props {
  jobDescription?: JobDescription;
  onGenerateQuestions: (selectedAttributes: string[]) => void;
}

export const BEIAttributesSidebar: React.FC<Props> = ({
  jobDescription,
  onGenerateQuestions,
}) => {
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);

  const handleAttributeToggle = (attribute: string) => {
    setSelectedAttributes((prev) =>
      prev.includes(attribute)
        ? prev.filter((attr) => attr !== attribute)
        : [...prev, attribute],
    );
  };

  const handleGenerateClick = () => {
    if (selectedAttributes.length > 0) {
      onGenerateQuestions(selectedAttributes);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: (theme) => `${theme.palette.primary.light}10`,
        border: '1px solid',
        borderColor: (theme) => `${theme.palette.primary.dark}40`,
        borderRadius: 2.5,
        p: { xs: 2, sm: 2.5 },
        maxWidth: { lg: 320 },
      }}
    >
      <Typography variant='h6' sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
        BEI Attributes
      </Typography>

      {/* Display attributes from job description behavioral attributes */}
      {jobDescription?.behavioralAttributes &&
        jobDescription.behavioralAttributes.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary', mb: 0.5 }}>
            Select attributes to generate questions:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              maxHeight: { xs: 200, lg: 'none' },
              overflowY: { xs: 'auto', lg: 'visible' },
              pr: { xs: 1, lg: 0 },
            }}
          >
            {jobDescription.behavioralAttributes.map((attribute, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    size='small'
                    checked={selectedAttributes.includes(attribute)}
                    onChange={() => handleAttributeToggle(attribute)}
                    sx={{ p: 0.5 }}
                  />
                }
                label={
                  <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {attribute}
                  </Typography>
                }
                sx={{ ml: 0, mr: 0, gap: 1, alignItems: 'flex-start' }}
              />
            ))}
          </Box>

          {/* Generate Button */}
          <Button
            variant='contained'
            onClick={handleGenerateClick}
            disabled={selectedAttributes.length === 0}
            fullWidth
            sx={{ mt: 1.5, textTransform: 'none', borderRadius: 2 }}
          >
            Generate Questions ({selectedAttributes.length})
          </Button>
        </Box>
      )}

      {/* STAR SCORING METHODOLOGY */}
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Typography variant='subtitle2' sx={{ color: 'primary.main', fontWeight: 600 }}>
          STAR Scoring Methodology
        </Typography>
        {STARSCORING.map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                opacity: 0.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 500 }}>
                {item.label}
              </Typography>
              <Typography variant='caption' sx={{ color: 'primary.dark', fontWeight: 700 }}>
                {item.value}%
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};
